import { group } from 'd3-array';
import flatten from 'lodash.flattendeep';
import { INormalizedPeriods, IPeriodData } from './normalizePeriodsToDate';
import { ITrendDataItem } from '../types';

export default trimTrendsByAllNull;

type IPeriodInterval = IPeriodData | ITrendDataItem;
/**
 * trimTrendsByAllNull removes data points from the start of trend line data arrays,
 * when all data points with matching dates (periodKeys) across trend line data arrays are also null.
 *
 * We only want to trim values where:
 *
 * - All the values with matching dates across trend lines are null
 * - All those null values with matching dates occur BEFORE the first non-null data point
 *
 * We do not want to trim values where:
 *
 * - They have a null value in the middle of the trend line, which can occur
 * - They occur at the end of the trend line
 *
 * The trend line data objects are assumed to have roughly the following structure:
 *
 * {
 *   entity: {},
 *   data: [ { periodKey: <timestamp>, date: Date, value: null/non-null value }, ... ],
 *   other metadata...,
 * }
 *
 * You can pass an array containing multiple arrays with trend line data objects, which is convenient
 * when you also want to map complementary trend lines (eg. for charts and social topics).
 *
 * @param  {Array} arr Array of arrays with trend line data objects
 * @return {Array}     The same array with trimmed trend line data.
 */
function trimTrendsByAllNull(
  arr: INormalizedPeriods[][]
): INormalizedPeriods[][] {
  if (!arr) return arr;

  // Mash it all together to get all data
  const all = flatten(
    arr.map((trends: INormalizedPeriods[]) =>
      trends.map((trend: INormalizedPeriods) => trend.data)
    )
  );

  // Sort the data ascending by date
  all.sort(
    (a: IPeriodInterval, b: IPeriodInterval) =>
      Number(a.periodKey) - Number(b.periodKey)
  );

  // Get the periodKeys where all values across trend lines are null
  const allValuesNull = getAllValuesNull(all);

  // Return the trends if no non-null values were found
  if (!allValuesNull) return arr;

  // Define a Array.prototype.filter callback that returns true when
  // this data point's periodKey is not included in allValuesNull
  const byIsNotAllNull = (d: IPeriodInterval): boolean =>
    !allValuesNull.includes(d.periodKey);

  return arr.map((trends) =>
    trends.map((obj: INormalizedPeriods) => ({
      ...obj,
      data: obj.data.filter(byIsNotAllNull),
    }))
  );
}

/**
 * getAllValuesNull returns an array of periodKeys of which all
 * data points across trend line data have a null value.
 *
 * @param  {Array} arr Flattened array of all data points in all trend lines
 * @return {Array}     Array of all dates where all values across all trend line data objects are null
 */
function getAllValuesNull(
  arr: IPeriodInterval[]
): (number | Date)[] | undefined {
  // Find always returns the first match from the array
  const firstNonNull = arr.find((d: IPeriodInterval) => d.value !== null);

  // If no firstNonNull was found, there is only null data, return.
  if (!firstNonNull) return;

  // Group the data into a Map by date
  // Ref: https://github.com/d3/d3-array#group
  const grouped = group(arr, (d: IPeriodInterval) => d.periodKey);

  // Find the dates where all values are null
  const allValuesNull = [];

  // Iterating over a Map with for ... of gives you an array of [key,value]
  // eslint-disable-next-line no-unused-vars
  for (const [key, value] of grouped) {
    // If every object in value has a null value
    if (value.every((d) => d.value === null)) {
      // And the key of this value is earlier than the first non-null periodKey
      if (key < firstNonNull.periodKey) {
        // This key is eligible for trimming
        allValuesNull.push(key);
      }
    }
  }

  return allValuesNull;
}
